'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { ImageWrappedProps } from '@eliadmin/core/src/components/image/image-wrapped';

import { useState } from 'react';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';

export function ProductGallery({ product }: { product: ProductDetailData }) {
  const allImages = [product.image, ...(product.images ?? [])];

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div className="hidden w-full max-w-[608px] gap-5 md:grid md:grid-cols-2">
        {allImages.map((image, index) => (
          <GalleryImage
            viewport="desktop"
            first={index === 0}
            onClick={handleOpenGallery(allImages, index)}
            key={index}
            src={image.url}
            width={0}
            height={0}
            alt={resolveImageAlt(image, product?.name)}
            className={cn(
              'aspect-square w-full object-contain object-center',
              (index === 0 || index % 3 === 0) && 'md:col-span-2',
            )}
            sizes={index === 0 || index % 3 === 0 ? '50vw' : '25vw'}
          />
        ))}
      </div>
      <div className="block w-full md:hidden">
        <CarouselProvider>
          <Carousel
            mobileCarouselDots={true}
            className={{
              mobileDotsConteiner:
                'absolute bottom-0 left-0 z-50 flex w-full items-center justify-center',
              container: 'w-full pb-8 md:hidden',
              main: 'w-full md:hidden',
              wrapper: 'w-full md:hidden',
              slide: 'flex h-[378px] w-full items-center justify-center sm:h-[450px]',
            }}
            slides={allImages.map((image, index) => (
              <GalleryImage
                viewport="mobile"
                first={index === 0}
                key={index}
                onClick={handleOpenGallery(allImages, index)}
                src={image.url}
                alt={resolveImageAlt(image, product?.name)}
                width={0}
                height={0}
                className="h-full w-auto cursor-pointer object-contain object-center"
                sizes="100vw"
              />
            ))}
          />
        </CarouselProvider>
      </div>
    </>
  );
}

// Keep the existing grid/flex image box while native picture selection prevents
// the hidden responsive gallery from downloading its real image before hydration.
const EMPTY_IMAGE = 'data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22/%3E';

function GalleryImage({
  viewport,
  first,
  ...props
}: ImageWrappedProps & { viewport: 'desktop' | 'mobile'; first: boolean }) {
  // Core adds a badge wrapper for AI images, so the img would no longer be a
  // direct picture child. Keep that existing badge/layout branch unchanged.
  if (typeof props.src === 'string' && /[?&]ai=1(?:&|$)/.test(props.src)) {
    return <Image {...props} />;
  }

  return (
    <picture className="contents">
      <source
        media={viewport === 'desktop' ? '(max-width: 767px)' : '(min-width: 768px)'}
        srcSet={EMPTY_IMAGE}
      />
      <Image
        {...props}
        loading={first ? 'eager' : undefined}
        fetchPriority={first ? 'high' : undefined}
      />
    </picture>
  );
}
